/**
 * Hubert acorn tree — replenishment rules for pinecones consumed by the bucket
 * or fire. Starting count and positions stay authored; the tree only refills
 * slots that have been permanently used.
 */

import type { PineconeInstanceRuntime } from '@/world-prototype/interaction/playerInteraction';

/** The large left tree — visually the pinecone source above the hammock lawn. */
export const PINECONE_TREE_ID = 'tree-left';

export const PINECONE_TREE_YARD_ID = 'hubert';

/** Enabled at game start — never change this list without updating tests. */
export const HUBERT_STARTING_PINECONE_IDS = [
  'pinecone-3',
  'pinecone-4',
  'pinecone-6',
  'pinecone-9',
  'pinecone-10',
] as const;

/** Disabled scatter defs reserved for tree drops (never placed at start). */
export const HUBERT_PINECONE_POOL_IDS = [
  'pinecone-1',
  'pinecone-2',
  'pinecone-5',
  'pinecone-7',
  'pinecone-8',
] as const;

export const HUBERT_MAX_USABLE_PINECONES = HUBERT_STARTING_PINECONE_IDS.length;

/** Prevent double-tap spam; short enough that kids do not notice a wait. */
export const PINECONE_TREE_COOLDOWN_MS = 1_500;

export const PINECONE_TREE_SHAKE_MS = 480;

/** Visible fall from canopy to lawn (ms). */
export const PINECONE_TREE_FALL_MS = 680;

/** Stagger between multiple drops from one shake (ms). */
export const PINECONE_TREE_FALL_STAGGER_MS = 140;

export function isPineconeTreeYard(yardId: string | null | undefined): boolean {
  return yardId === PINECONE_TREE_YARD_ID;
}

export function isPineconePoolId(id: string): boolean {
  return (HUBERT_PINECONE_POOL_IDS as readonly string[]).includes(id);
}

/** Usable = still in the world and recoverable. */
export function isUsablePineconeState(
  state: PineconeInstanceRuntime['state'],
): boolean {
  return state === 'ground' || state === 'carried' || state === 'thrown';
}

export function countUsablePinecones(
  runtime: ReadonlyMap<string, PineconeInstanceRuntime>,
): number {
  let count = 0;
  for (const pine of runtime.values()) {
    if (isUsablePineconeState(pine.state)) count += 1;
  }
  return count;
}

/** How many more pinecones the yard may hold right now. */
export function replacementEligibleCount(
  runtime: ReadonlyMap<string, PineconeInstanceRuntime>,
): number {
  return Math.max(
    0,
    HUBERT_MAX_USABLE_PINECONES - countUsablePinecones(runtime),
  );
}

/** 2–3 per shake, never more than currently eligible. */
export function replacementDropCount(
  eligible: number,
  rng: () => number = Math.random,
): number {
  if (eligible <= 0) return 0;
  const batch = 2 + Math.floor(rng() * 2);
  return Math.min(eligible, batch);
}

/** Largest rendered pinecone in the yard — replacement copies this size. */
export function largestPineconeDisplaySize(
  runtime: ReadonlyMap<string, PineconeInstanceRuntime>,
): { width: number; height: number } {
  let bestArea = 0;
  let width = 0.008;
  let height = 0.012;
  for (const pine of runtime.values()) {
    if (!isUsablePineconeState(pine.state) && pine.state !== 'bucket') continue;
    const area = pine.width * pine.height;
    if (area > bestArea) {
      bestArea = area;
      width = pine.width;
      height = pine.height;
    }
  }
  return { width, height };
}

export function pickUnusedPoolIds(
  runtime: ReadonlyMap<string, PineconeInstanceRuntime>,
  count: number,
): string[] {
  const picks: string[] = [];
  for (const id of HUBERT_PINECONE_POOL_IDS) {
    if (runtime.has(id)) continue;
    picks.push(id);
    if (picks.length >= count) break;
  }
  return picks;
}

/** Release points in the lower/middle canopy of the left tree. */
export function pineconeCanopyOrigins(
  count: number,
  treeX: number,
  treeY: number,
  rng: () => number = Math.random,
): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < count; i += 1) {
    const spread = (i - (count - 1) / 2) * 0.018;
    out.push({
      x: treeX + spread + (rng() - 0.5) * 0.024,
      y: treeY - 0.045 - rng() * 0.035,
    });
  }
  return out;
}

/** Ease-in fall from canopy toward a lawn landing spot. */
export function samplePineconeTreeFall(
  from: { x: number; y: number },
  land: { x: number; y: number },
  elapsedMs: number,
  durationMs = PINECONE_TREE_FALL_MS,
): { x: number; y: number; rotationDeg: number; landed: boolean } {
  const t = Math.min(1, Math.max(0, elapsedMs / durationMs));
  const ease = t * t;
  const spin = (land.x - from.x) * 140;
  return {
    x: from.x + (land.x - from.x) * ease,
    y: from.y + (land.y - from.y) * ease,
    rotationDeg: spin * ease,
    landed: t >= 1,
  };
}

/** Lawn spots near the left tree where replacements land. */
export function pineconeDropPositions(
  count: number,
  rng: () => number = Math.random,
): { x: number; y: number }[] {
  const center = { x: 0.46, y: 0.59 };
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = (i / Math.max(1, count)) * Math.PI * 2 + rng() * 0.6;
    const dist = 0.028 + rng() * 0.042;
    out.push({
      x: center.x + Math.cos(angle) * dist,
      y: center.y + Math.sin(angle) * dist * 0.55,
    });
  }
  return out;
}
